import { useSnackbar } from '@/context/SnackbarContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InsightsDateRange } from '../insights/insightsTypes';
import { API_ENDPOINTS, buildApiUrl } from '@/constants';
import { api, getErrorMessage } from '@/utils';
import { AxiosError } from 'axios';
import { InsightsJobStatus, JobStatus } from './useFetchInsightsJobStatus';

interface StartInsightsJobResponse {
    jobId: number;
    status: JobStatus;
    alreadyActive: boolean;
}

export const INSIGHTS_JOB_STATUS_QUERY_KEY = ['global-insights-job-status'] as const;

// Key holding the id of the last re-run started from this browser.
// Kept in localStorage so InsightsJobWatcher still recognises the run after a refresh.
export const INSIGHTS_PENDING_RUN_KEY = 'global-insights-pending-run';

const useGenerateInsights = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({
            dateRange,
            projectIds,
        }: {
            dateRange: InsightsDateRange;
            projectIds?: number[];
        }): Promise<StartInsightsJobResponse> => {
            const params: Record<string, string | number> = {
                date_range: dateRange,
            };

            if (projectIds?.length) {
                params.project_id = projectIds.join(',');
            }

            const url = buildApiUrl(API_ENDPOINTS.GLOBAL_PROMPT_INSIGHTS, params);

            const response = await api.post<StartInsightsJobResponse>(url);

            return response.data;
        },

        onSuccess: data => {
            localStorage.setItem(INSIGHTS_PENDING_RUN_KEY, String(data.jobId));

            // Show the active state immediately, without waiting for the first poll.
            queryClient.setQueryData<InsightsJobStatus>(INSIGHTS_JOB_STATUS_QUERY_KEY, {
                jobId: data.jobId,
                status: data.status,
            });

            // Confirm the current status with the server.
            void queryClient.invalidateQueries({
                queryKey: INSIGHTS_JOB_STATUS_QUERY_KEY,
            });

            if (data.alreadyActive) {
                showSnackbar('A re-run is already in progress', 'info');
            }
        },

        onError: (error: AxiosError) => {
            if (error.response?.status === 409) {
                void queryClient.invalidateQueries({
                    queryKey: INSIGHTS_JOB_STATUS_QUERY_KEY,
                });

                showSnackbar('A re-run is already in progress. Please try again.', 'info');
                return;
            }

            showSnackbar(getErrorMessage(error) ?? 'Failed to start re-run', 'error');
        },
    });
};

export default useGenerateInsights;
